import React, { useState } from 'react';
import { addCamera, updateCamera, deleteCamera, startCamera, stopCamera } from '../api';

// SVG icons
const PlusIcon = ({ size = 24, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);
const CogsIcon = ({ size = 24, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v4" />
    <path d="M12 18v4" />
    <path d="M4.93 4.93l2.83 2.83" />
    <path d="M16.24 16.24l2.83 2.83" />
    <path d="M2 12h4" />
    <path d="M18 12h4" />
    <path d="M4.93 19.07l2.83-2.83" />
    <path d="M16.24 7.76l2.83-2.83" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const PlayIcon = ({ size = 16, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5,3 19,12 5,21" />
  </svg>
);

const StopIcon = ({ size = 16, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="6" width="12" height="12" />
  </svg>
);

const CameraManagement = ({ cameras, user, onUpdateCameras }) => {
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [error, setError] = useState(null);
  const [phoneValidation, setPhoneValidation] = useState({});
  const [toggleStates, setToggleStates] = useState({});
  
  // Form state for controlled inputs
  const [formData, setFormData] = useState({
    name: '',
    rtsp_url: '0',
    interval: 5,
    prompt: 'Analyze this image carefully. If you detect a girl or woman in the frame, return ALERT: Girl detected in camera view. Otherwise, return OK: No girl detected.',
    contacts: ''
  });

  // Phone number validation function
  const validatePhoneNumber = (phoneNumber) => {
    // Remove any spaces or special characters except + and digits
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // Check if it starts with + and has 10-15 digits
    if (!cleaned.startsWith('+')) {
      return { valid: false, message: 'Must start with +' };
    }
    
    // Remove the + and check if it's all digits
    const digits = cleaned.substring(1);
    if (!digits.match(/^\d+$/)) {
      return { valid: false, message: 'Must contain only digits after +' };
    }
    
    // Check length (10-15 digits is typical for international numbers)
    if (digits.length < 10) {
      return { valid: false, message: 'Too short (minimum 10 digits)' };
    }
    
    if (digits.length > 15) {
      return { valid: false, message: 'Too long (maximum 15 digits)' };
    }
    
    return { valid: true, message: 'Valid phone number' };
  };

  // Validate all phone numbers in a comma-separated string
  const validatePhoneNumbers = (phoneString) => {
    if (!phoneString.trim()) {
      return { valid: true, message: 'No phone numbers entered' };
    }
    
    const phones = phoneString.split(',').map(phone => phone.trim());
    const results = phones.map(phone => ({
      phone,
      ...validatePhoneNumber(phone)
    }));
    
    const invalidPhones = results.filter(result => !result.valid);
    
    if (invalidPhones.length === 0) {
      return { valid: true, message: `All ${phones.length} phone numbers are valid` };
    } else {
      return { 
        valid: false, 
        message: `Invalid numbers: ${invalidPhones.map(p => p.phone).join(', ')}`,
        invalidPhones: invalidPhones
      };
    }
  };

  const handleAddCamera = async (e) => {
    e.preventDefault();
    
    // Validate phone numbers before submitting
    const phoneValidation = validatePhoneNumbers(formData.contacts);
    
    if (!phoneValidation.valid) {
      setError(`Phone number validation failed: ${phoneValidation.message}`);
      return;
    }
    
    const newCamera = {
      name: formData.name,
      rtsp_url: formData.rtsp_url,
      interval: parseInt(formData.interval, 10),
      prompt: formData.prompt,
      contacts: formData.contacts.split(',').map(c => c.trim()).filter(c => c),
    };
    
    try {
      console.log('📷 [FRONTEND] Adding camera with validated phone numbers:', newCamera.contacts);
      await addCamera(newCamera);
      resetForm();
      onUpdateCameras();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleUpdateCamera = async (e) => {
    e.preventDefault();
    
    // Validate phone numbers before submitting
    const phoneValidation = validatePhoneNumbers(formData.contacts);
    
    if (!phoneValidation.valid) {
      setError(`Phone number validation failed: ${phoneValidation.message}`);
      return;
    }
    
    const updatedCamera = {
      name: formData.name,
      rtsp_url: formData.rtsp_url,
      interval: parseInt(formData.interval, 10),
      prompt: formData.prompt,
      contacts: formData.contacts.split(',').map(c => c.trim()).filter(c => c),
    };
    
    try {
      console.log('📷 [FRONTEND] Updating camera with validated phone numbers:', updatedCamera.contacts);
      await updateCamera(selectedCamera.id, updatedCamera);
      resetForm();
      onUpdateCameras();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteCamera = async (id) => {
    try {
      await deleteCamera(id);
      onUpdateCameras();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleToggleCamera = async (camera) => {
    const cameraId = camera.id;
    const isCurrentlyConnected = camera.connection_status === 'Connected';
    
    // Set loading state
    setToggleStates(prev => ({ ...prev, [cameraId]: 'loading' }));
    
    try {
      if (isCurrentlyConnected) {
        console.log('🛑 [CAMERA] Stopping camera:', cameraId);
        await stopCamera(cameraId);
        setToggleStates(prev => ({ ...prev, [cameraId]: 'stopped' }));
      } else {
        console.log('🚀 [CAMERA] Starting camera:', cameraId);
        await startCamera(cameraId);
        setToggleStates(prev => ({ ...prev, [cameraId]: 'started' }));
      }
      
      // Refresh cameras to get updated status
      setTimeout(() => {
        onUpdateCameras();
        setToggleStates(prev => ({ ...prev, [cameraId]: null }));
      }, 1000);
      
    } catch (error) {
      console.error('❌ [CAMERA] Toggle error:', error);
      setError(`Failed to ${isCurrentlyConnected ? 'stop' : 'start'} camera: ${error.message}`);
      setToggleStates(prev => ({ ...prev, [cameraId]: 'error' }));
      setTimeout(() => {
        setToggleStates(prev => ({ ...prev, [cameraId]: null }));
      }, 3000);
    }
  };

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      name: '',
      rtsp_url: '0',
      interval: 5,
      prompt: 'Analyze this image carefully. If you detect a girl or woman in the frame, return ALERT: Girl detected in camera view. Otherwise, return OK: No girl detected.',
      contacts: ''
    });
    setSelectedCamera(null);
    setError(null);
    setPhoneValidation({});
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle camera selection for editing
  const handleEditCamera = (camera) => {
    setSelectedCamera(camera);
    setError(null);
    
    // Populate form with camera data
    setFormData({
      name: camera.name || '',
      rtsp_url: camera.rtsp_url || '0',
      interval: camera.interval || 5,
      prompt: camera.prompt || 'Analyze this image carefully. If you detect a girl or woman in the frame, return ALERT: Girl detected in camera view. Otherwise, return OK: No girl detected.',
      contacts: (camera.contacts || []).join(', ')
    });
    
    // Validate existing phone numbers when editing
    const contactsString = (camera.contacts || []).join(', ');
    const validation = validatePhoneNumbers(contactsString);
    setPhoneValidation(validation);
  };

  // Handle phone number input change with real-time validation
  const handlePhoneInputChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      contacts: value
    }));
    
    const validation = validatePhoneNumbers(value);
    setPhoneValidation(validation);
    
    // Clear error if validation passes
    if (validation.valid && error && error.includes('Phone number validation failed')) {
      setError(null);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
      <h3 className="text-2xl font-semibold mb-6 text-white flex items-center">
        <CogsIcon className="mr-2 text-gray-400" /> Camera Management
      </h3>
      
      <form onSubmit={selectedCamera ? handleUpdateCamera : handleAddCamera} className="mb-8 space-y-5">
        <h4 className="text-lg font-semibold text-gray-300">{selectedCamera ? 'Edit Camera' : 'Add New Camera'}</h4>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-400 mb-1">Camera Name</label>
          <input 
            type="text" 
            name="name" 
            placeholder="e.g., Front Door" 
            className="p-3 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
            value={formData.name}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-400 mb-1">Camera Source</label>
          <input 
            type="text" 
            name="rtsp_url" 
            placeholder="0 (Desktop Camera) or rtsp://user:pass@host:554/stream" 
            className="p-3 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
            value={formData.rtsp_url}
            onChange={handleInputChange}
            required 
          />
          <p className="text-xs text-gray-500 mt-1">Use "0" for desktop camera, "1" for second camera, or RTSP URL for network cameras</p>
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-400 mb-1">Interval (seconds)</label>
          <input 
            type="number" 
            name="interval" 
            placeholder="e.g., 5" 
            className="p-3 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
            value={formData.interval}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-400 mb-1">AI Prompt</label>
          <textarea 
            name="prompt" 
            rows="3" 
            placeholder="Describe what to detect. Include ALERT/OK logic." 
            className="p-3 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
            value={formData.prompt}
            onChange={handleInputChange}
            required
          ></textarea>
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-400 mb-1">Alert Contacts (comma-separated)</label>
          <input 
            type="text" 
            name="contacts" 
            placeholder="e.g., +12345678901, +972523381612" 
            className={`p-3 border rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 ${
              phoneValidation.valid 
                ? 'border-green-500 focus:ring-green-500' 
                : phoneValidation.message && phoneValidation.message !== 'No phone numbers entered'
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-600 focus:ring-blue-500'
            }`}
            value={formData.contacts}
            onChange={handlePhoneInputChange}
            required 
          />
          {phoneValidation.message && (
            <div className={`text-xs mt-1 ${
              phoneValidation.valid 
                ? 'text-green-400' 
                : 'text-red-400'
            }`}>
              {phoneValidation.message}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-1">
            💡 Format: +12345678901 (10-15 digits, start with +)
          </div>
        </div>
        {error && <p className="text-red-400 text-sm italic mb-4 text-center">{error}</p>}
        <div className="flex justify-end space-x-3">
          <button 
            type="submit" 
            disabled={!phoneValidation.valid && phoneValidation.message && phoneValidation.message !== 'No phone numbers entered'}
            className={`font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center ${
              !phoneValidation.valid && phoneValidation.message && phoneValidation.message !== 'No phone numbers entered'
                ? 'bg-gray-500 cursor-not-allowed text-gray-300'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            <PlusIcon className="mr-2" />
            {selectedCamera ? 'Save Changes' : 'Add Camera'}
          </button>
          {selectedCamera && (
            <button type="button" onClick={resetForm} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200">
              Cancel
            </button>
          )}
        </div>
      </form>

      <h4 className="text-xl font-semibold text-gray-300 mb-4">My Cameras</h4>
      {cameras.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-300">
            <thead className="text-xs text-gray-400 uppercase bg-gray-700">
              <tr>
                <th className="px-4 py-3">Camera Name</th>
                <th className="px-4 py-3">Camera Source</th>
                <th className="px-4 py-3">Frame Capture Interval</th>
                <th className="px-4 py-3">Fixed Prompt Example</th>
                <th className="px-4 py-3">WhatsApp Contact Numbers</th>
                <th className="px-4 py-3">Connection Status</th>
                <th className="px-4 py-3">Control</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cameras.map((cam, idx) => (
                <tr key={cam.id || idx} className="bg-gray-800 border-b border-gray-700 hover:bg-gray-750">
                  <td className="px-4 py-3 font-medium text-white">{cam.name}</td>
                  <td className="px-4 py-3 text-gray-300 font-mono text-xs">
                    {cam.rtsp_url === '0' ? 'Desktop Camera (0)' : 
                     cam.rtsp_url === '1' ? 'Second Camera (1)' : 
                     cam.rtsp_url === '2' ? 'Third Camera (2)' : 
                     cam.rtsp_url}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{cam.interval} seconds</td>
                  <td className="px-4 py-3 text-gray-300 max-w-xs">
                    <div className="truncate" title={cam.prompt}>
                      {cam.prompt.length > 50 ? `${cam.prompt.substring(0, 50)}...` : cam.prompt}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    <div className="space-y-1">
                      {cam.contacts && cam.contacts.length > 0 ? (
                        cam.contacts.map((contact, contactIdx) => (
                          <div key={contactIdx} className="text-xs bg-gray-700 px-2 py-1 rounded">
                            {contact}
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-500 text-xs">No contacts</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      cam.connection_status === 'Connected' 
                        ? 'bg-green-900 text-green-300' 
                        : 'bg-red-900 text-red-300'
                    }`}>
                      {cam.connection_status || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(cam.rtsp_url === '0' || cam.rtsp_url === 0) ? (
                      <button
                        onClick={() => handleToggleCamera(cam)}
                        disabled={toggleStates[cam.id] === 'loading'}
                        className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                          toggleStates[cam.id] === 'loading'
                            ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                            : cam.connection_status === 'Connected'
                            ? 'bg-red-600 hover:bg-red-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                        title={cam.connection_status === 'Connected' ? 'Stop monitoring' : 'Start monitoring'}
                      >
                        {toggleStates[cam.id] === 'loading' ? (
                          <span className="inline-block w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin mr-1"></span>
                        ) : cam.connection_status === 'Connected' ? (
                          <StopIcon size={14} className="mr-1" />
                        ) : (
                          <PlayIcon size={14} className="mr-1" />
                        )}
                        {toggleStates[cam.id] === 'loading' 
                          ? 'Loading...' 
                          : cam.connection_status === 'Connected' 
                          ? 'Stop' 
                          : 'Start'}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-500 italic">N/A (Network Camera)</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditCamera(cam)} 
                        className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-xs transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteCamera(cam.id)} 
                        className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 rounded text-xs transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-700 p-8 rounded-lg text-center">
          <p className="text-gray-500 text-sm">You have no cameras added yet.</p>
          <p className="text-gray-400 text-xs mt-2">Click "Add Camera" above to get started.</p>
        </div>
      )}
    </div>
  );
};

export default CameraManagement;